import React, { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';
import { AuthContext } from '../context/AuthContext';
import Modal from '../components/Modal';
import apiService from '../services/apiService';
import { Country, State } from 'country-state-city';
import AISpecialistMatcher from './AISpecialistMatcher';

const Specialists = () => {
  const { data: users, isLoading, error } = useApi('/users/userRole/specialist');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const { state } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useContext(AuthContext);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const [searchQuery, setSearchQuery] = useState(state?.searchQuery || '');
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState(state?.availabilityFilter || '');
  const [feesFilter, setFeesFilter] = useState([0, 500]);
  const [genderFilter, setGenderFilter] = useState(state?.genderFilter || '');
  const [countryFilter, setCountryFilter] = useState(state?.countryFilter || '');
  const [stateFilter, setStateFilter] = useState('');
  const [availableStates, setAvailableStates] = useState([]);

  // ── AI filter state ──────────────────────────────────────────────────────────
  const [aiMatchedIds, setAiMatchedIds] = useState(null);   // null = inactive, [] or [...] = active
  const [aiReasoningMap, setAiReasoningMap] = useState({});  // { _id: "reason string" }
  const [aiSummary, setAiSummary] = useState('');
  // ─────────────────────────────────────────────────────────────────────────────

  const specialtyOptions = [
    { value: 'poultry_care', label: 'Poultry Care' },
    { value: 'cattle_health', label: 'Cattle Health' },
    { value: 'veterinary_surgery', label: 'Veterinary Surgery' },
    { value: 'animal_nutrition', label: 'Animal Nutrition' },
    { value: 'livestock_management', label: 'Livestock Management' },
    { value: 'disease_control', label: 'Disease Control' },
    { value: 'farm_vaccination', label: 'Farm Vaccination' },
    { value: 'poultry_diagnostics', label: 'Poultry Diagnostics' },
    { value: 'avian_diseases', label: 'Avian Diseases Specialist' },
    { value: 'hatchery_management', label: 'Hatchery Management' },
    { value: 'broiler_management', label: 'Broiler Management' },
    { value: 'layer_management', label: 'Layer Farm Management' },
    { value: 'biosecurity_management', label: 'Biosecurity Management' },
    { value: 'feed_formulation', label: 'Feed Formulation' },
    { value: 'parasite_control', label: 'Parasite Control' },
    { value: 'antibiotic_stewardship', label: 'Antibiotic Stewardship' },
    { value: 'reproductive_health', label: 'Reproductive Health' },
    { value: 'egg_quality_management', label: 'Egg Quality Management' },
    { value: 'farm_sanitation', label: 'Farm Sanitation & Hygiene' },
    { value: 'environmental_management', label: 'Poultry Environment Management' },
    { value: 'heat_stress_management', label: 'Heat Stress Management' },
    { value: 'poultry_welfare', label: 'Animal Welfare (Poultry)' },
    { value: 'veterinary_consultation', label: 'General Veterinary Consultation' },
  ];

  const getSpecialtyLabels = (specialties) => {
    return specialties.map((specialty) =>
      specialtyOptions.find((option) => option.value === specialty)?.label || specialty
    );
  };

  const formatRating = (rating) => {
    if (rating == null || isNaN(Number(rating))) return '4.67';
    return Number(rating).toFixed(2);
  };

  const formatFees = (fees) => {
    if (fees == null || isNaN(Number(fees))) return '0.00';
    return Number(fees).toFixed(2);
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setSpecialtyFilter('');
    setAvailabilityFilter('');
    setFeesFilter([0, 500]);
    setGenderFilter('');
    setCountryFilter('');
    setStateFilter('');
    setAiMatchedIds(null);
    setAiReasoningMap({});
    setAiSummary('');
  };

  // ── AI filter handlers ───────────────────────────────────────────────────────
  const handleAiMatchResults = ({ matchedIds, summary, reasoningMap }) => {
    setAiMatchedIds(matchedIds);
    setAiReasoningMap(reasoningMap || {});
    setAiSummary(summary || '');
  };

  const handleClearAiFilter = () => {
    setAiMatchedIds(null);
    setAiReasoningMap({});
    setAiSummary('');
  };
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    let filtered = users || [];

    // If AI filter is active, start from AI-ranked order
    if (aiMatchedIds !== null) {
      const matchedSet = new Set(aiMatchedIds);
      // Preserve AI ranking order
      filtered = aiMatchedIds
        .map((id) => filtered.find((u) => u._id === id))
        .filter(Boolean);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.name?.toLowerCase().includes(query) ||
          user.specialties?.some((spec) => spec.toLowerCase().includes(query)) ||
          user.bio?.toLowerCase().includes(query)
      );
    }

    if (specialtyFilter) {
      filtered = filtered.filter((user) => user.specialties?.includes(specialtyFilter));
    }

    if (availabilityFilter) {
      filtered = filtered.filter((user) => user.availability === availabilityFilter);
    }

    filtered = filtered.filter((user) => {
      const fees = user.fees != null ? Number(user.fees) : 0;
      return fees >= feesFilter[0] && fees <= feesFilter[1];
    });

    if (genderFilter && genderFilter !== 'all') {
      filtered = filtered.filter((user) => user.gender?.toLowerCase() === genderFilter.toLowerCase());
    }

    if (countryFilter) {
      filtered = filtered.filter((user) => user.country === countryFilter);
    }

    if (stateFilter) {
      filtered = filtered.filter((user) => user.state === stateFilter);
    }

    setFilteredUsers(filtered);
  }, [users, searchQuery, specialtyFilter, availabilityFilter, feesFilter, genderFilter, countryFilter, stateFilter, aiMatchedIds]);

  useEffect(() => {
    if (countryFilter) {
      const states = State.getStatesOfCountry(countryFilter).map((state) => ({
        isoCode: state.isoCode,
        name: state.name,
      }));
      setAvailableStates(states);
      setStateFilter('');
    } else {
      setAvailableStates([]);
      setStateFilter('');
    }
  }, [countryFilter]);

  const handleBookNow = async (specialist) => {
    if (!isAuthenticated()) {
      setIsLoginModalOpen(true);
      return;
    }

    if (user.role !== 'farmer') {
      try {
        const subResponse = await apiService.get(`/stripe/subscription-status?userId=${user.id}`, { timeout: 10000 });
        if (!subResponse.data && !subResponse.data.active) {
          setIsSubscriptionModalOpen(true);
          return;
        }
      } catch (err) {
        console.error('Error checking subscription status:', err.message);
        setIsSubscriptionModalOpen(true);
        return;
      }
    }

    navigate(`/book/${specialist._id}`, { state: { doctor: specialist } });
  };

  const handleLoginClose = () => {
    setIsLoginModalOpen(false);
    navigate('/login');
  };

  const handleSubscriptionClose = () => {
    setIsSubscriptionModalOpen(false);
  };

  const handleSubscriptionRedirect = () => {
    setIsSubscriptionModalOpen(false);
    navigate('/subscriptions');
  };

  if (isLoading) {
    return (
      <div className="text-center text-gray-600 text-lg font-medium py-12">
        Loading specialists...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 text-lg font-medium py-12">
        Error loading specialists: {error.message}
      </div>
    );
  }

  const uniqueSpecialties = [...new Set(users?.flatMap((user) => user.specialties).filter(Boolean))];
  const isAiFilterActive = aiMatchedIds !== null;

  return (
    <div className="relative w-full max-w-7xl mx-auto py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-primary text-center mb-8 tracking-tight bg-gradient-to-r from-secondary/20 to-transparent rounded-lg py-3">
        Our Specialists
      </h1>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Side Panel for Filters */}
        <div className="w-full lg:w-1/4 bg-white rounded-xl shadow-xl p-6 sm:p-8 border border-secondary/10">
          <div className="flex justify-between items-center mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-semibold text-primary">Filters</h2>
            <button
              onClick={handleResetFilters}
              className="flex items-center gap-1 text-secondary text-sm sm:text-base font-medium hover:text-opacity-90 transition duration-200"
            >
              <span>↺</span> Reset
            </button>
          </div>
          <div className="space-y-6">
            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">Search</label>
              <input
                type="text"
                placeholder="Search by name or specialty"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">Specialty</label>
              <select
                value={specialtyFilter}
                onChange={(e) => setSpecialtyFilter(e.target.value)}
                className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
              >
                <option value="">All Specialties</option>
                {uniqueSpecialties.map((spec, index) => (
                  <option key={index} value={spec}>{getSpecialtyLabels([spec])[0]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">Availability</label>
              <select
                value={availabilityFilter}
                onChange={(e) => setAvailabilityFilter(e.target.value)}
                className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
              >
                <option value="">All</option>
                <option value="available">Available</option>
                <option value="unavailable">Not Available</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">Fees Range</label>
              <div className="flex gap-3">
                <input
                  type="number"
                  min="0"
                  value={feesFilter[0]}
                  onChange={(e) => setFeesFilter([parseFloat(e.target.value) || 0, feesFilter[1]])}
                  className="w-1/2 p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
                  placeholder="Min"
                />
                <input
                  type="number"
                  min="0"
                  value={feesFilter[1]}
                  onChange={(e) => setFeesFilter([feesFilter[0], parseFloat(e.target.value) || 500])}
                  className="w-1/2 p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
                  placeholder="Max"
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">Country</label>
              <select
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
              >
                <option value="">All Countries</option>
                {Country.getAllCountries().map((country) => (
                  <option key={country.isoCode} value={country.isoCode}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">State</label>
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
                disabled={!countryFilter}
              >
                <option value="">All States</option>
                {availableStates.map((state) => (
                  <option key={state.isoCode} value={state.isoCode}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">Gender</label>
              <select
                value={genderFilter}
                onChange={(e) => setGenderFilter(e.target.value)}
                className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
              >
                <option value="">All</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Specialists Grid */}
        <div className="w-full lg:w-3/4">

          {/* ── AI Filter Active Banner ── */}
          {isAiFilterActive && (
            <div
              style={{
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: 12,
                padding: '12px 16px',
                marginBottom: 20,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <span style={{ fontSize: 18, flexShrink: 0 }}>🤖</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#166534', marginBottom: 2 }}>
                  AI Match Active — {filteredUsers.length} specialist{filteredUsers.length !== 1 ? 's' : ''} matched
                </div>
                {aiSummary && (
                  <div style={{ fontSize: 12, color: '#374151' }}>{aiSummary}</div>
                )}
              </div>
              <button
                onClick={handleClearAiFilter}
                style={{
                  background: 'none',
                  border: '1px solid #86efac',
                  borderRadius: 7,
                  padding: '3px 10px',
                  fontSize: 11,
                  color: '#166534',
                  cursor: 'pointer',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                Clear ✕
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
            {filteredUsers.map((user, rankIndex) => (
              <div
                key={user._id}
                className="group bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col h-full"
                style={
                  isAiFilterActive
                    ? { border: '1.5px solid #86efac', position: 'relative' }
                    : {}
                }
              >
                {/* AI Match rank badge */}
                {isAiFilterActive && (
                  <div style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 10,
                    background: '#166534',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '3px 9px',
                    borderRadius: 20,
                    letterSpacing: '0.03em',
                  }}>
                    🤖 #{rankIndex + 1} AI Match
                  </div>
                )}

                {/* Image Section */}
                <div className="relative">
                  <img
                    src={
                      user.profilePicture
                        ? `${BASE_URL}${user.profilePicture}`
                        : 'https://via.placeholder.com/500x500'
                    }
                    alt={user.name || 'Specialist'}
                    className="w-full h-64 sm:h-72 md:h-80 object-cover"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>

                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-md px-3 py-1 rounded-full shadow-md flex items-center gap-1 text-sm font-semibold text-amber-500">
                    ★ {formatRating(user.rating) || '4.8'}
                  </div>

                  <div
                    className={`absolute bottom-4 left-4 px-3 py-1 rounded-full text-xs sm:text-sm font-medium shadow-md ${
                      user.availability === 'available'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-red-600 text-white'
                    }`}
                  >
                    {user.availability === 'available' ? '● Available Now' : '● Not Available'}
                  </div>

                  <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-md shadow-lg px-4 py-2 rounded-2xl border border-gray-100">
                    <p className="text-xs text-gray-500 font-medium leading-none">Starting From</p>
                    <p className="text-lg sm:text-xl font-bold text-secondary leading-tight">
                      £{formatFees(user.fees)}
                    </p>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-5 sm:p-6 flex flex-col flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 line-clamp-1">
                    {user.name || 'Unknown Specialist'}
                  </h3>

                  <p className="text-sm text-gray-500 mt-1 mb-3">
                    {user.experience != null
                      ? `${user.experience}+ years experience`
                      : 'Experience not specified'}
                  </p>

                  {/* AI reasoning snippet */}
                  {isAiFilterActive && aiReasoningMap[user._id] && (
                    <div style={{
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: 8,
                      padding: '7px 10px',
                      marginBottom: 12,
                      fontSize: 11,
                      color: '#166534',
                      lineHeight: 1.5,
                    }}>
                      🤖 {aiReasoningMap[user._id]}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 mb-6">
                    {getSpecialtyLabels(user.specialties || [])
                      .slice(0, 3)
                      .map((specialty, idx) => (
                        <span
                          key={idx}
                          className="bg-secondary/10 text-secondary text-xs font-semibold px-3 py-1 rounded-full"
                        >
                          {specialty}
                        </span>
                      ))}
                    {(user.specialties?.length || 0) > 3 && (
                      <span className="text-gray-400 text-xs self-center">
                        +{user.specialties.length - 3} more
                      </span>
                    )}
                  </div>

                  <button
                    onClick={() => handleBookNow(user)}
                    disabled={user.availability !== 'available'}
                    className="mt-auto w-full py-3.5 rounded-2xl bg-secondary text-white font-semibold text-sm sm:text-base hover:bg-secondary/90 transition-all duration-300 shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed active:scale-[0.98]"
                  >
                    Book Appointment
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredUsers.length === 0 && (
            <p className="text-center text-gray-500 text-lg font-medium py-12">
              {isAiFilterActive
                ? 'No AI-matched specialists found for your criteria. Try describing your situation differently.'
                : 'No specialists match your filters.'}
            </p>
          )}
        </div>
      </div>

      {/* ── AI Specialist Matcher floating widget ── */}
      <AISpecialistMatcher
        specialists={users || []}
        onMatchResults={handleAiMatchResults}
        onClear={handleClearAiFilter}
        isActive={isAiFilterActive}
      />

      <Modal isOpen={isLoginModalOpen} onClose={handleLoginClose} title="Login Required">
        <p className="text-gray-600 text-sm sm:text-base mb-4">
          Please log in to book an appointment with a veterinary specialist.
        </p>
        <button
          onClick={handleLoginClose}
          className="w-full bg-secondary text-white px-4 py-2.5 sm:py-3 rounded-lg hover:bg-opacity-90 transition duration-300 font-semibold text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 shadow-sm"
        >
          Go to Login
        </button>
      </Modal>

      <Modal isOpen={isSubscriptionModalOpen} onClose={handleSubscriptionClose} title="Subscription Required">
        <p className="text-gray-600 text-sm sm:text-base mb-4">
          An active subscription is required to book an appointment. Please subscribe to continue.
        </p>
        <div className="flex justify-between space-x-4">
          <button
            onClick={handleSubscriptionClose}
            className="flex-1 bg-gray-400 text-white px-4 py-2.5 sm:py-3 rounded-lg hover:bg-gray-500 transition duration-300 font-semibold text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 shadow-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubscriptionRedirect}
            className="flex-1 bg-secondary text-white px-4 py-2.5 sm:py-3 rounded-lg hover:bg-opacity-90 transition duration-300 font-semibold text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 shadow-sm"
          >
            Subscribe Now
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Specialists;
